import React, { useState, useEffect, useContext, useMemo } from 'react'

import LoadingContext from '../../context/Loading/Loading'

import Button from 'react-bootstrap/Button'
import Modal from 'react-bootstrap/Modal'
import Form from 'react-bootstrap/Form'
import Card from 'react-bootstrap/Card'
import Row from 'react-bootstrap/Row'
import Col from 'react-bootstrap/Col'

import AuthService from '../../utilities/services/auth.service'
import UserContext from '../../context/User/User'
import { ORGANIZATION_PERMISSION_SLUGS } from '@blocktickets/shared-utils'
import { checkPermission } from '../../utilities/helpers'
import {
  getFnbLocations,
  createFnbLocation,
  updateFnbLocation,
  deleteFnbLocation,
  getOrgVenues,
} from '../../utilities/api'

import LocationQrModal, {
  buildLocationMenuUrl,
  buildSeatDeliveryMenuUrl,
} from './LocationQrModal'

import { LoadingContainer } from '../LoadingContainer'
import { PageLoadingContainer } from '../PageLoadingContainer'
import { NoPermissionsContainer } from '../NoPermissionsContainer'

import '../BuyerTypesWrapper/buyerTypesWrapper.scss'
import '../MarketplaceWrapper/marketplace.scss'

const LOCATION_TYPES = ['concession', 'bar', 'suite', 'portable', 'kiosk']

const EMPTY_FORM = {
  name: '',
  code: '',
  venueUuid: '',
  sectionName: '',
  type: 'concession',
  isActive: true,
}

function parseSectionName(sectionId) {
  if (!sectionId) return ''
  return (
    String(sectionId)
      .split(',')
      .map((part) => part.trim())
      .find(Boolean) ?? ''
  )
}

function extractSectionsFromVenue(venue) {
  const sectionMap = new Map()

  ;(venue?.rooms || []).forEach((room) => {
    ;(room?.seatmaps || []).forEach((seatmap) => {
      const sections = seatmap?.mapping?.sections || {}
      Object.values(sections).forEach((section) => {
        const value = String(
          section?.sectionNumber || section?.sectionName || ''
        ).trim()
        if (!value || sectionMap.has(value)) return
        sectionMap.set(value, {
          value,
          label: String(
            section?.sectionName || section?.sectionNumber || value
          ),
        })
      })
    })
  })

  return [...sectionMap.values()].sort((a, b) =>
    a.label.localeCompare(b.label, undefined, {
      numeric: true,
      sensitivity: 'base',
    })
  )
}

function buildSectionOptions(venueSections, selectedName = '') {
  const sectionMap = new Map()
  venueSections.forEach((section) => {
    sectionMap.set(section.value, section)
  })
  if (selectedName && !sectionMap.has(selectedName)) {
    sectionMap.set(selectedName, {
      value: selectedName,
      label: selectedName,
    })
  }
  return [...sectionMap.values()].sort((a, b) =>
    a.label.localeCompare(b.label, undefined, {
      numeric: true,
      sensitivity: 'base',
    })
  )
}

function getRowAttrs(row) {
  return row.attributes ?? row
}

function hasMarketplacePermission(getPermissions, orgPermissionsReady) {
  if (!orgPermissionsReady) return false
  const perms = getPermissions()
  return (
    checkPermission(perms, ORGANIZATION_PERMISSION_SLUGS.MANAGE_MARKETPLACE) ||
    checkPermission(perms, ORGANIZATION_PERMISSION_SLUGS.EVENT_MANAGEMENT)
  )
}

export default function MarketplaceLocationsWrapper() {
  const { isLoading, showLoading, hideLoading } = useContext(LoadingContext)
  const { orgPermissions } = useContext(UserContext)
  const { getPermissions } = AuthService

  const orgUUID = AuthService.getOrg()[0]?.uuid
  const orgPermissionsReady = orgPermissions?.length > 0
  const hasPermission = hasMarketplacePermission(
    getPermissions,
    orgPermissionsReady
  )

  const [locations, setLocations] = useState([])
  const [venues, setVenues] = useState([])
  const [isLoadingVenues, setIsLoadingVenues] = useState(true)
  const [isLoadingData, setIsLoadingData] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [editingId, setEditingId] = useState(null)
  const [saving, setSaving] = useState(false)
  const [form, setForm] = useState(EMPTY_FORM)
  const [qrLocation, setQrLocation] = useState(null)

  const requiresVenueSelection = venues.length > 1
  const defaultVenueUuid = venues.length === 1 ? (venues[0]?.uuid ?? '') : ''

  const venueNameByUuid = useMemo(() => {
    const map = new Map()
    venues.forEach((venue) => {
      if (venue?.uuid) {
        map.set(venue.uuid, venue.name ?? venue.uuid)
      }
    })
    return map
  }, [venues])

  const selectedVenue = useMemo(
    () =>
      venues.find(
        (venue) => venue.uuid === (form.venueUuid || defaultVenueUuid)
      ) ?? null,
    [venues, form.venueUuid, defaultVenueUuid]
  )

  const venueSections = useMemo(
    () => extractSectionsFromVenue(selectedVenue),
    [selectedVenue]
  )

  const sectionOptions = useMemo(
    () => buildSectionOptions(venueSections, form.sectionName),
    [venueSections, form.sectionName]
  )

  const openQr = (row) => {
    const attrs = getRowAttrs(row)
    const sectionName = parseSectionName(attrs.sectionId)
    setQrLocation({
      id: row.id,
      name: attrs.name ?? 'Location',
      seatDeliveryUrl:
        attrs.venue_uuid && sectionName
          ? buildSeatDeliveryMenuUrl(orgUUID, attrs.venue_uuid, sectionName)
          : '',
    })
  }

  const closeQr = () => setQrLocation(null)

  const load = () => {
    setIsLoadingData(true)
    getFnbLocations(orgUUID)
      .then((res) => setLocations(res?.data?.data ?? []))
      .catch((err) => console.error(err))
      .finally(() => {
        setIsLoadingData(false)
        hideLoading()
      })
  }

  const loadVenues = () => {
    setIsLoadingVenues(true)
    getOrgVenues(orgUUID)
      .then((res) => {
        const rows = Array.isArray(res?.data) ? res.data : []
        setVenues(rows)
      })
      .catch((err) => console.error(err))
      .finally(() => setIsLoadingVenues(false))
  }

  useEffect(() => {
    if (!orgPermissionsReady) return
    if (!hasPermission) {
      hideLoading()
      return
    }
    if (!orgUUID) return
    showLoading()
    load()
    loadVenues()
  }, [orgPermissionsReady, hasPermission, orgUUID])

  const openAdd = () => {
    setEditingId(null)
    setForm({
      ...EMPTY_FORM,
      venueUuid: defaultVenueUuid,
    })
    setShowModal(true)
  }

  const openEdit = (row) => {
    const a = getRowAttrs(row)
    setEditingId(row.id)
    setForm({
      name: a.name ?? '',
      code: a.code ?? '',
      venueUuid: a.venue_uuid ?? defaultVenueUuid,
      sectionName: parseSectionName(a.sectionId),
      type: a.type ?? 'concession',
      isActive: a.isActive !== false,
    })
    setShowModal(true)
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    if (!orgUUID || !form.name.trim()) return

    const venueUuid = form.venueUuid || defaultVenueUuid
    if (requiresVenueSelection && !venueUuid) return

    setSaving(true)
    const payload = {
      organization_uuid: orgUUID,
      venue_uuid: venueUuid || null,
      name: form.name.trim(),
      code: form.code.trim(),
      sectionId: form.sectionName.trim() || null,
      type: form.type,
      isActive: form.isActive,
    }
    const done = (createdRow) => {
      setShowModal(false)
      load()
      if (createdRow?.id) {
        openQr(createdRow)
      }
    }
    const req = editingId
      ? updateFnbLocation(editingId, payload)
      : createFnbLocation(payload)
    req
      .then((res) => done(editingId ? null : res?.data?.data))
      .catch((err) => console.error(err))
      .finally(() => setSaving(false))
  }

  const handleDelete = (row) => {
    const a = getRowAttrs(row)
    if (!window.confirm(`Delete location “${a.name}”?`)) return
    deleteFnbLocation(row.id)
      .then(() => load())
      .catch((err) => console.error(err))
  }

  const canSubmit =
    Boolean(form.name.trim()) &&
    (!requiresVenueSelection || Boolean(form.venueUuid || defaultVenueUuid))

  if (!orgPermissionsReady) return <PageLoadingContainer />
  if (!hasPermission) return <NoPermissionsContainer />
  if (isLoading) return <PageLoadingContainer />

  return (
    <>
      <div className="marketplace-layout">
        <div className="marketplace-toolbar">
          <p className="mb-0 text-muted">
            Concession stands, bars, and carts where items can be sold.
          </p>
          <Button variant="primary" onClick={openAdd}>
            + Add location
          </Button>
        </div>

        {locations.length === 0 && !isLoadingData ? (
          <Card body className="card--sm">
            <div className="marketplace-empty">
              <p className="marketplace-empty__message">No locations yet.</p>
              <Button variant="primary" onClick={openAdd}>
                + Add location
              </Button>
            </div>
          </Card>
        ) : isLoadingData ? (
          <LoadingContainer />
        ) : (
          <div className="buyer-types-table-wrap">
            <table className="buyer-types-table">
              <thead>
                <tr>
                  <th scope="col">Name</th>
                  <th scope="col">Code</th>
                  {requiresVenueSelection ? <th scope="col">Venue</th> : null}
                  <th scope="col">Venue section</th>
                  <th scope="col">Type</th>
                  <th scope="col">Status</th>
                  <th
                    scope="col"
                    className="buyer-types-table__actions"
                    aria-label="Actions"
                  />
                </tr>
              </thead>
              <tbody>
                {locations.map((row) => {
                  const a = getRowAttrs(row)
                  return (
                    <tr key={row.id}>
                      <td className="buyer-types-table__name">{a.name}</td>
                      <td>{a.code || '—'}</td>
                      {requiresVenueSelection ? (
                        <td>
                          {a.venue_uuid
                            ? (venueNameByUuid.get(a.venue_uuid) ?? '—')
                            : '—'}
                        </td>
                      ) : null}
                      <td>{a.sectionId || '—'}</td>
                      <td className="marketplace-locations-table__type">
                        {a.type ?? '—'}
                      </td>
                      <td>
                        <span
                          className={`marketplace-status ${
                            a.isActive
                              ? 'marketplace-status--active'
                              : 'marketplace-status--inactive'
                          }`}
                        >
                          {a.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="buyer-types-table__actions">
                        <Button
                          variant="link"
                          className="p-0 me-2"
                          onClick={() => openQr(row)}
                          title="View and print location QR codes"
                        >
                          QR code
                        </Button>
                        <Button
                          variant="link"
                          className="p-0 me-2"
                          onClick={() => openEdit(row)}
                        >
                          Edit
                        </Button>
                        <Button
                          variant="link"
                          className="p-0 text-danger"
                          onClick={() => handleDelete(row)}
                        >
                          Delete
                        </Button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Modal
        show={showModal}
        onHide={() => !saving && setShowModal(false)}
        centered
      >
        <Form onSubmit={handleSubmit}>
          <Modal.Header closeButton>
            <Modal.Title>
              {editingId ? 'Edit location' : 'Add location'}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Row className="g-3">
              <Col xs={12}>
                <Form.Group controlId="loc-name">
                  <Form.Label>Name</Form.Label>
                  <Form.Control
                    value={form.name}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, name: e.target.value }))
                    }
                    placeholder="Main Concession Stand"
                    required
                  />
                </Form.Group>
              </Col>
              <Col sm={6}>
                <Form.Group controlId="loc-code">
                  <Form.Label>Code</Form.Label>
                  <Form.Control
                    value={form.code}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, code: e.target.value }))
                    }
                    placeholder="MAIN"
                  />
                </Form.Group>
              </Col>
              {requiresVenueSelection ? (
                <Col xs={12}>
                  <Form.Group controlId="loc-venue">
                    <Form.Label>Venue</Form.Label>
                    {isLoadingVenues ? (
                      <div className="text-muted small">Loading venues...</div>
                    ) : (
                      <Form.Select
                        value={form.venueUuid}
                        onChange={(e) =>
                          setForm((f) => ({
                            ...f,
                            venueUuid: e.target.value,
                            sectionName: '',
                          }))
                        }
                        required
                      >
                        <option value="">Select a venue</option>
                        {venues.map((venue) => (
                          <option key={venue.uuid} value={venue.uuid}>
                            {venue.name}
                          </option>
                        ))}
                      </Form.Select>
                    )}
                    <Form.Text className="text-muted">
                      Locations are scoped to a venue so section names stay
                      unique across your organization.
                    </Form.Text>
                  </Form.Group>
                </Col>
              ) : null}
              <Col xs={12}>
                <Form.Group controlId="loc-section">
                  <Form.Label>Venue section</Form.Label>
                  {isLoadingVenues ? (
                    <div className="text-muted small">
                      Loading seatmap sections...
                    </div>
                  ) : requiresVenueSelection && !form.venueUuid ? (
                    <div className="text-muted small">
                      Select a venue to load its seatmap sections.
                    </div>
                  ) : sectionOptions.length === 0 ? (
                    <div className="text-muted small">
                      No sections found on this venue&apos;s seatmaps. Assign a
                      seatmap to a venue room to load section names.
                    </div>
                  ) : (
                    <Form.Select
                      value={form.sectionName}
                      onChange={(e) =>
                        setForm((f) => ({
                          ...f,
                          sectionName: e.target.value,
                        }))
                      }
                    >
                      <option value="">Select a venue section</option>
                      {sectionOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Form.Select>
                  )}
                  <Form.Text className="text-muted">
                    Seat-delivery orders are routed to locations whose venue
                    section matches this section name.
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col sm={6}>
                <Form.Group controlId="loc-type">
                  <Form.Label>Type</Form.Label>
                  <Form.Select
                    value={form.type}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, type: e.target.value }))
                    }
                  >
                    {LOCATION_TYPES.map((t) => (
                      <option key={t} value={t}>
                        {t.charAt(0).toUpperCase() + t.slice(1)}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col xs={12}>
                <Form.Check
                  type="switch"
                  id="loc-active"
                  label="Active"
                  checked={form.isActive}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, isActive: e.target.checked }))
                  }
                />
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => setShowModal(false)}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={saving || !canSubmit}
            >
              {saving ? 'Saving…' : editingId ? 'Save' : 'Add'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <LocationQrModal
        show={Boolean(qrLocation)}
        onHide={closeQr}
        locationName={qrLocation?.name}
        menuUrl={
          qrLocation?.id ? buildLocationMenuUrl(orgUUID, qrLocation.id) : ''
        }
        seatDeliveryUrl={qrLocation?.seatDeliveryUrl || ''}
      />
    </>
  )
}
